import React, { useState, useEffect, useCallback } from "react";
import type { SetViewerOption } from "../../schemas/book-schemas";
import { ViewerOptionToggles } from "./ViewerOptionToggles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileService } from "../../../services/fileService";
import toast from "react-hot-toast";

interface FileApiResponse {
  _id: string;
  name: string;
  path: string;
  extension: string;
  size: string;
  user_id: string;
  original_name: string;
  host: string;
  storage: string;
  type: string;
  options?: {
    s_base_file_path?: string;
    s_base_imsmanifest?: string;
  };
  url: string;
  updated_at: string;
  created_at: string;
}

interface ContentFileProps {
  sessionId: string;
  sessionName: string;
  onContentUpload?: (
    sessionId: string,
    file: File,
    contentType: string,
    fileData?: FileApiResponse,
    blockIndex?: number
  ) => void;
  onContentDelete?: (sessionId: string, contentId: string) => void;
  /** Per-block viewer switches; blockIndex is undefined for a section's single content_block */
  onSetViewerOption?: SetViewerOption;
  blockIndex?: number;
  currentFile?: {
    file_id: string;
    file_name: string;
    file_type: string;
    file_size: number;
    file_url?: string;      // legacy direct URL
    view_url?: string;      // preferred for viewing/preview
    download_url?: string;  // optional download URL
    file_path?: string;     // backend-internal; do not use to build URLs on FE
  };
  contentBlock?: {
    content_type: 'html' | 'pdf' | 'scorm' | 'html_package' | 'pptx' | 'media' | 'quiz' | 'video' | 'audio' | 'image' | 'embed' | 'text' | 'list' | 'flashcard';
    payload: any;
  };
}

export const ContentFile: React.FC<ContentFileProps> = ({
  sessionId,
  sessionName,
  onContentUpload,
  onContentDelete,
  onSetViewerOption,
  blockIndex,
  currentFile,
  contentBlock,
}) => {
  const [selectedContentType, setSelectedContentType] =
    useState<string>(contentBlock?.content_type || "scorm");
  const [isUploading, setIsUploading] = useState(false);
  const [scormUrl, setScormUrl] = useState<string>("");
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [userCancelledEdit, setUserCancelledEdit] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewRefreshToken, setPreviewRefreshToken] = useState<number>(0);

  const resolvedContentType =
    contentBlock?.content_type || currentFile?.file_type || selectedContentType;

  // Prefer backend-provided view_url for any viewing/preview, fall back to file_url for legacy data
  const storedViewUrl =
    contentBlock?.payload?.view_url ||
    currentFile?.view_url ||
    contentBlock?.payload?.file_url ||
    currentFile?.file_url ||
    "";
  const storedLink = storedViewUrl || "";
  const fileIdForPreview =
    contentBlock?.payload?.file_id || currentFile?.file_id || "";
  const storageType =
    contentBlock?.payload?.storage ||
    (storedLink && storedLink.includes("/api/secure-file")
      ? "secure"
      : "");
  const isSecureFile = storageType === "secure";
  const normalizedContentType = (resolvedContentType || "").toLowerCase();
  const previewableTypes = ["image", "video", "audio"];
  const isPreviewableType = previewableTypes.includes(normalizedContentType);
  const canRetryPreview = isSecureFile && isPreviewableType;
  const canOpenPreview = isSecureFile
    ? !!fileIdForPreview
    : !!(previewUrl || storedLink);

  // Function to check if content_block has valid payload
  const hasValidPayload = (contentBlock?: any) => {
    if (!contentBlock || !contentBlock.payload) return false;
    
    const { content_type, payload } = contentBlock;

    const viewUrl =
      payload.view_url ||
      payload.file_url ||
      "";

    const hasFileLink =
      typeof viewUrl === "string" &&
      viewUrl.trim() !== "";
    
    switch (content_type) {
      case 'html':
        return payload.html && payload.html.trim() !== '' && payload.html !== '<p>Nhập nội dung HTML của bạn tại đây...</p>';
      case 'pdf':
        return hasFileLink;
      case 'video':
        return hasFileLink;
      case 'audio':
        return hasFileLink;
      case 'image':
        return hasFileLink;
      case 'scorm':
        return hasFileLink;
      case 'html_package':
        return hasFileLink;
      case 'pptx':
        return hasFileLink;
      case 'quiz':
        return true; // Quiz is always valid, even with no questions
      case 'embed':
        return payload.url && payload.url !== null && payload.url !== '' && payload.url.trim() !== '';
      default:
        return false;
    }
  };

  // Check if we should show edit mode by default
  const shouldShowEditMode = !currentFile?.file_id && (!contentBlock || !hasValidPayload(contentBlock));
  
  // Force edit mode if content_block exists but has no valid file_path/file_url
  const forceEditMode = contentBlock && !hasValidPayload(contentBlock);

  // Update selected content type when contentBlock changes
  useEffect(() => {
    if (contentBlock?.content_type) {
      setSelectedContentType(contentBlock.content_type);
    }
  }, [contentBlock?.content_type]);

  // Also update when contentBlock changes (not just content_type)
  useEffect(() => {
    if (contentBlock?.content_type) {
      setSelectedContentType(contentBlock.content_type);
    }
  }, [contentBlock]);

  useEffect(() => {
    let isMounted = true;

    const noFileAvailable = !fileIdForPreview && !storedLink;
    console.debug("[ContentFile] Preview effect run", {
      fileIdForPreview,
      storedLink,
      isPreviewableType,
      isSecureFile,
      noFileAvailable,
      contentType: resolvedContentType,
      previewRefreshToken,
    });
    if (!isPreviewableType || noFileAvailable) {
      if (isMounted) {
        const nextUrl = noFileAvailable ? null : storedLink || null;
        console.debug("[ContentFile] Non-previewable or no file available, using URL", {
          nextUrl,
        });
        setPreviewUrl(nextUrl);
        setPreviewError(null);
        setIsPreviewLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }

    if (!isSecureFile) {
      if (isMounted) {
        const nextUrl = storedLink || null;
        console.debug("[ContentFile] Non-secure file preview URL resolved", {
          nextUrl,
        });
        setPreviewUrl(nextUrl);
        setPreviewError(null);
        setIsPreviewLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }

    if (!fileIdForPreview) {
      if (isMounted) {
        console.error("[ContentFile] Missing fileIdForPreview for secure preview", {
          fileIdForPreview,
          storedLink,
        });
        setPreviewUrl(null);
        setPreviewError("Không thể tạo bản xem trước do thiếu mã tệp.");
        setIsPreviewLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }

    console.debug("[ContentFile] Fetching secure file preview URL", {
      fileIdForPreview,
    });
    setIsPreviewLoading(true);
    setPreviewError(null);

    FileService.getSecureFilePreviewUrl(fileIdForPreview)
      .then((url) => {
        if (!isMounted) return;
        console.debug("[ContentFile] Secure file preview URL fetched", {
          fileIdForPreview,
          url,
        });
        setPreviewUrl(url);
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error("[ContentFile] Failed to fetch secure file preview URL", {
          fileIdForPreview,
          error,
        });
        setPreviewUrl(null);
        setPreviewError("Không thể tải bản xem trước. Vui lòng thử lại.");
      })
      .finally(() => {
        if (!isMounted) return;
        setIsPreviewLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [
    fileIdForPreview,
    isPreviewableType,
    isSecureFile,
    storedLink,
    previewRefreshToken,
  ]);


  const contentTypes = [
    {
      value: "scorm",
      label: "SCORM",
      icon: "📦",
      color:
        "bg-gradient-to-br from-purple-100 to-violet-100 text-purple-800 border-purple-300 dark:from-purple-900/30 dark:to-violet-900/30 dark:text-purple-300 dark:border-purple-600",
      accept: ".zip",
    },
    {
      value: "html_package",
      label: "Học liệu HTML",
      icon: "🌐",
      color:
        "bg-gradient-to-br from-sky-100 to-blue-100 text-sky-800 border-sky-300 dark:from-sky-900/30 dark:to-blue-900/30 dark:text-sky-300 dark:border-sky-600",
      accept: ".html,.htm",
    },
    { value: 'image', label: 'Image', icon: '🖼️', color: 'bg-gradient-to-br from-green-100 to-emerald-100 text-green-800 border-green-300 dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-300 dark:border-green-600', accept: '.jpg,.jpeg,.png,.gif,.bmp,.tiff,.ico,.webp' },
    { value: 'video', label: 'Video', icon: '🎥', color: 'bg-gradient-to-br from-blue-100 to-cyan-100 text-blue-800 border-blue-300 dark:from-blue-900/30 dark:to-cyan-900/30 dark:text-blue-300 dark:border-blue-600', accept: '.mp4,.mov,.avi,.wmv,.flv,.webm' },
    { value: 'audio', label: 'Audio', icon: '🎵', color: 'bg-gradient-to-br from-orange-100 to-amber-100 text-orange-800 border-orange-300 dark:from-orange-900/30 dark:to-amber-900/30 dark:text-orange-300 dark:border-orange-600', accept: '.mp3,.wav,.ogg,.m4a,.aac,.flac,.wma' },
    // { value: 'pdf', label: 'PDF', icon: '📄', color: 'bg-gradient-to-br from-red-100 to-rose-100 text-red-800 border-red-300 dark:from-red-900/30 dark:to-rose-900/30 dark:text-red-300 dark:border-red-600', accept: '.pdf' },
    // { value: 'epub', label: 'EPUB', icon: '📚', color: 'bg-gradient-to-br from-green-100 to-teal-100 text-green-800 border-green-300 dark:from-green-900/30 dark:to-teal-900/30 dark:text-green-300 dark:border-green-600', accept: '.epub' },
  ];

  const selectedType = contentTypes.find(
    (type) => type.value === selectedContentType
  );

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Upload file using secure FileService
      const response = await FileService.uploadFileSecure(file, selectedContentType);

      if (response && response.success && response.result && response.result.length > 0) {
        const secureFileData = response.result[0];
        
        // Convert secure file data to legacy format for compatibility
        const fileData = {
          _id: secureFileData._id,
          name: secureFileData.name,
          path: secureFileData.name, // Use name as path for compatibility
          extension: secureFileData.extension,
          size: secureFileData.size,
          user_id: "current_user", // Will be set by backend
          original_name: secureFileData.original_name,
          host: process.env.REACT_APP_API_URL || '',
          storage: secureFileData.storage,
          type: secureFileData.type,
          url: secureFileData.url, // Use original URL
          video_metadata: secureFileData.video_metadata,
          // Học liệu HTML cần entry_path server tìm được trong gói
          options: secureFileData.options,
          updated_at: secureFileData.updated_at,
          created_at: secureFileData.created_at,
        };

        // Call onContentUpload with file data if provided
        if (onContentUpload) {
          await onContentUpload(sessionId, file, selectedContentType, fileData, blockIndex);
        }

        // Show success message
        setIsEditMode(false); // Exit edit mode after successful upload
        setUserCancelledEdit(false); // Reset cancellation state after successful upload
      } else {
        throw new Error("No file data in response");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Có lỗi xảy ra";
      toast.error(`Upload thất bại: ${errorMessage}`);
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  const handleUrlSubmit = async () => {
    if (!scormUrl.trim()) {
      toast.error("Vui lòng nhập URL SCORM");
      return;
    }

    // Validate URL format
    try {
      new URL(scormUrl);
    } catch {
      toast.error("URL không hợp lệ. Vui lòng nhập URL đúng định dạng.");
      return;
    }

    setIsUploading(true);
    try {
      // Create a mock file data for URL-based SCORM with secure token
      const mockFileData = {
        _id: `url_${Date.now()}`,
        name: "SCORM URL",
        path: scormUrl,
        extension: "url",
        size: "0",
        user_id: "system",
        original_name: "SCORM URL",
        host: "external",
        storage: "url",
        type: "scorm",
        url: scormUrl,
        updated_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        // Add secure token for consistency
        secure_token: `url_token_${Date.now()}`,
        download_url: scormUrl,
        view_url: scormUrl,
      };

      // Call onContentUpload with mock file data
      if (onContentUpload) {
        await onContentUpload(
          sessionId,
          new File([], "scorm-url"),
          "scorm",
          mockFileData,
          blockIndex
        );
      }

      setScormUrl("");
      setIsEditMode(false); // Exit edit mode after successful URL addition
      setUserCancelledEdit(false); // Reset cancellation state after successful URL addition
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Có lỗi xảy ra";
      toast.error(`Thêm URL thất bại: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteContent = () => {
    if (onContentDelete && currentFile?.file_id) {
      onContentDelete(sessionId, currentFile.file_id);
      setIsEditMode(false); // Exit edit mode after deletion
    }
  };

  const handleRefreshPreview = useCallback(() => {
    setPreviewError(null);
    setPreviewUrl(null);
    setPreviewRefreshToken((prev) => prev + 1);
  }, []);

  const handleOpenPreview = useCallback(async () => {
    try {
      if (isSecureFile) {
        if (!fileIdForPreview) {
          toast.error("Không tìm thấy mã tệp để xem trước.");
          return;
        }
        await FileService.openSecureFileById(fileIdForPreview);
        return;
      }

      const targetUrl = previewUrl || storedLink;
      if (targetUrl) {
        window.open(targetUrl, "_blank", "noopener");
      } else {
        toast.error("Không có URL để xem trước.");
      }
    } catch (error) {
      console.error("Failed to open preview:", error);
      toast.error("Không thể mở bản xem trước. Vui lòng thử lại.");
    }
  }, [fileIdForPreview, isSecureFile, previewUrl, storedLink]);


  const enterEditMode = () => {
    setIsEditMode(true);
    setUserCancelledEdit(false); // Reset cancellation state when entering edit mode
    
    // Set the content type based on existing content
    if (contentBlock?.content_type) {
      setSelectedContentType(contentBlock.content_type);
    } else if (currentFile?.file_type) {
      setSelectedContentType(currentFile.file_type);
    }
    
    setScormUrl("");
  };

  const exitEditMode = () => {
    setIsEditMode(false);
    setUserCancelledEdit(true); // Remember that user cancelled edit mode
    setScormUrl("");
  };

  const hasContent = currentFile?.file_id || (contentBlock && hasValidPayload(contentBlock));

  return (
    <div className="space-y-4">
      {/* Persistent Top Bar - Always Visible */}
      <div className="bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-900/20 dark:to-slate-900/20 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
        {/* Top Bar - Icon, Title, and Actions */}
        <div className="flex items-center justify-between px-4 py-3 bg-white/50 dark:bg-gray-800/30 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center shadow-sm ${
              hasContent 
                ? "bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30"
                : "bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30"
            }`}>
              <span className="text-lg">
                {hasContent ? (
                  contentBlock?.content_type === "scorm" || contentBlock?.content_type === "embed"
                    ? "🔗"
                    : contentBlock?.content_type === "pdf"
                    ? "📄"
                    : contentBlock?.content_type === "video"
                    ? "🎥"
                    : contentBlock?.content_type === "audio"
                    ? "🎵"
                    : contentBlock?.content_type === "image"
                    ? "🖼️"
                    : contentBlock?.content_type === "quiz"
                    ? "❓"
                    : contentBlock?.content_type === "pptx"
                    ? "📊"
                    : contentBlock?.content_type === "media"
                    ? "🎨"
                    : contentBlock?.content_type === "html"
                    ? "🌐"
                    : contentBlock?.content_type === "html_package"
                    ? "🌐"
                    : "📦"
                ) : (
                  selectedType?.icon || "📦"
                )}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                {hasContent ? (
                  contentBlock?.payload?.file_name 
                    ? contentBlock.payload.file_name
                    : contentBlock?.content_type === "scorm" || contentBlock?.content_type === "embed"
                    ? "SCORM"
                    : contentBlock?.content_type === "pdf"
                    ? "PDF"
                    : contentBlock?.content_type === "video"
                    ? "Video"
                    : contentBlock?.content_type === "audio"
                    ? "Audio"
                    : contentBlock?.content_type === "image"
                    ? "Hình ảnh"
                    : contentBlock?.content_type === "quiz"
                    ? "Bài kiểm tra"
                    : contentBlock?.content_type === "pptx"
                    ? "Slide PowerPoint"
                    : contentBlock?.content_type === "media"
                    ? "Đa phương tiện"
                    : contentBlock?.content_type === "html"
                    ? "HTML"
                    : contentBlock?.content_type === "html_package"
                    ? "Học liệu HTML"
                    : currentFile?.file_name || "Nội dung"
                ) : (
                  isEditMode ? `Thêm ${selectedType?.label || "Nội dung"}` : sessionName || "Nội dung"
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {hasContent && (
              <ViewerOptionToggles
                sessionId={sessionId}
                blockIndex={blockIndex}
                block={contentBlock}
                onSetViewerOption={onSetViewerOption}
              />
            )}
            {hasContent && (
              <Button
                variant="ghost"
                size="sm"
                className={`h-9 px-3 text-xs font-medium rounded-lg transition-all duration-200 ${
                  !isEditMode
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                    : "text-gray-600 hover:text-green-600 hover:bg-green-100 dark:text-gray-400 dark:hover:text-green-400 dark:hover:bg-green-900/30"
                }`}
                onClick={() => setIsEditMode(false)}
                title={!isEditMode ? "Đang xem nội dung" : "Chuyển sang chế độ xem"}
                disabled={!isEditMode}
              >
                <svg
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                Xem
              </Button>
            )}
            {hasContent && !isEditMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 px-3 text-xs font-medium rounded-lg transition-all duration-200 text-gray-600 hover:text-blue-600 hover:bg-blue-100 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-900/30"
                  onClick={enterEditMode}
                  title="Chỉnh sửa nội dung"
                >
                  <svg
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Sửa
                </Button>
            )}
            {hasContent && isEditMode && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 px-3 text-xs font-medium rounded-lg transition-all duration-200 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                  disabled
                  title="Đang chỉnh sửa"
                >
                  <svg
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Sửa
                </Button>
            )}
            {hasContent && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 text-gray-600 hover:text-red-600 hover:bg-red-100 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200"
                onClick={handleDeleteContent}
                title="Xóa nội dung"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </Button>
            )}
            {!hasContent && !isEditMode && (
              <Button
                variant="outline"
                size="sm"
                className="h-9 px-4 text-xs font-medium shadow-sm hover:shadow-md transition-all duration-200"
                onClick={enterEditMode}
              >
                ➕ Thêm nội dung
              </Button>
            )}
            {!hasContent && isEditMode && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 text-gray-600 hover:text-red-600 hover:bg-red-100 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200"
                onClick={exitEditMode}
                title="Hủy"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </Button>
            )}
          </div>
        </div>

        {/* Content Area */}
        {hasContent && (
          <>
            {/* Preview Mode Content */}
            {!isEditMode && (
            <div className="p-4 bg-gradient-to-br from-green-50/50 to-emerald-50/50 dark:from-green-900/10 dark:to-emerald-900/10">
            {/* Show URL only for external SCORM URLs, not for uploaded files */}
            {(contentBlock?.content_type === "scorm" || contentBlock?.content_type === "embed") && 
             contentBlock?.payload?.file_name === "SCORM URL" && 
             (contentBlock?.payload?.view_url || contentBlock?.payload?.file_url) ? (
              <p className="text-xs text-blue-600 dark:text-blue-400 break-all mb-3">
                🔗 {contentBlock.payload.view_url || contentBlock.payload.file_url}
              </p>
            ) : contentBlock?.content_type === "embed" && contentBlock?.payload?.url ? (
              <p className="text-xs text-blue-600 dark:text-blue-400 break-all mb-3">
                🔗 {contentBlock.payload.url}
              </p>
            ) : currentFile?.file_type === "url" && (currentFile?.view_url || currentFile?.file_url) ? (
              <p className="text-xs text-blue-600 dark:text-blue-400 break-all mb-3">
                🔗 {currentFile.view_url || currentFile.file_url}
              </p>
            ) : (!contentBlock?.payload?.file_name &&
                (contentBlock?.payload?.view_url || contentBlock?.payload?.file_url)) ? (
              <p className="text-xs text-gray-600 dark:text-gray-400 break-all mb-3">
                📄 {(contentBlock.payload.view_url || contentBlock.payload.file_url).split('/').pop() ||
                  contentBlock.payload.view_url || contentBlock.payload.file_url}
              </p>
            ) : null}
            
            {/* Preview for media content */}
            {contentBlock?.content_type === "image" &&
              (contentBlock?.payload?.file_url || fileIdForPreview) && (
                <div className="space-y-2">
                  {isPreviewLoading && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                      <svg className="animate-spin h-3 w-3 mr-1.5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Đang tải bản xem trước...
                    </p>
                  )}
                  {previewUrl && !previewError && (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full max-w-xs h-40 object-cover rounded-lg border-2 border-green-200 dark:border-green-700 shadow-sm"
                      onError={(e) => {
                        console.error("[ContentFile] Image preview failed to load", {
                          src: e.currentTarget.src,
                          previewUrl,
                          fileIdForPreview,
                          storedLink,
                          contentType: resolvedContentType,
                        });
                        e.currentTarget.style.display = "none";
                        setPreviewError("Không thể tải bản xem trước hình ảnh.");
                      }}
                    />
                  )}
                  {previewError && (
                    <div className="flex items-center space-x-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                      <span>{previewError}</span>
                      {canRetryPreview && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs hover:bg-red-100 dark:hover:bg-red-900/30"
                          onClick={handleRefreshPreview}
                          disabled={isPreviewLoading}
                        >
                          Thử lại
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

            {contentBlock?.content_type === "video" &&
              (contentBlock?.payload?.file_url || fileIdForPreview) && (
                <div className="space-y-2">
                  {isPreviewLoading && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                      <svg className="animate-spin h-3 w-3 mr-1.5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Đang tải bản xem trước...
                    </p>
                  )}
                  {previewUrl && !previewError && (
                    <video
                      src={previewUrl}
                      controls
                      className="w-full max-w-xs h-40 rounded-lg border-2 border-green-200 dark:border-green-700 shadow-sm"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        setPreviewError("Không thể tải bản xem trước video.");
                      }}
                    >
                      Trình duyệt của bạn không hỗ trợ video.
                    </video>
                  )}
                  {previewError && (
                    <div className="flex items-center space-x-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                      <span>{previewError}</span>
                      {canRetryPreview && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs hover:bg-red-100 dark:hover:bg-red-900/30"
                          onClick={handleRefreshPreview}
                          disabled={isPreviewLoading}
                        >
                          Thử lại
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

            {contentBlock?.content_type === "audio" &&
              (contentBlock?.payload?.file_url || fileIdForPreview) && (
                <div className="space-y-2">
                  {isPreviewLoading && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                      <svg className="animate-spin h-3 w-3 mr-1.5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Đang tải bản xem trước...
                    </p>
                  )}
                  {previewUrl && !previewError && (
                    <div className="p-3 bg-white dark:bg-gray-700 rounded-lg border-2 border-green-200 dark:border-green-700 shadow-sm">
                      <audio
                        src={previewUrl}
                        controls
                        className="w-full max-w-xs"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          setPreviewError("Không thể tải bản xem trước audio.");
                        }}
                      >
                        Trình duyệt của bạn không hỗ trợ audio.
                      </audio>
                    </div>
                  )}
                  {previewError && (
                    <div className="flex items-center space-x-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                      <span>{previewError}</span>
                      {canRetryPreview && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs hover:bg-red-100 dark:hover:bg-red-900/30"
                          onClick={handleRefreshPreview}
                          disabled={isPreviewLoading}
                        >
                          Thử lại
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Edit Mode Content */}
          {isEditMode && (
            <div className="p-4 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10">

          {/* SCORM Upload/URL Mode */}
          {selectedContentType === "scorm" && (
            <div className="p-4">
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <span className="mr-2">📦</span>
                  {currentFile?.file_id || (contentBlock && hasValidPayload(contentBlock)) ? "Thay thế SCORM Package" : "Tải lên SCORM Package"}
                </h4>
              </div>

              {/* Show current file info when editing */}
              {(currentFile?.file_id || (contentBlock && hasValidPayload(contentBlock))) && (
                <div className="mb-4 p-3 bg-blue-100 dark:bg-blue-900/40 rounded-lg border border-blue-300 dark:border-blue-700">
                  <p className="text-xs font-medium text-blue-900 dark:text-blue-100 mb-1">File hiện tại:</p>
                  <p className="text-sm text-blue-800 dark:text-blue-200 font-semibold">
                    {contentBlock?.payload?.file_name || currentFile?.file_name || "SCORM"}
                  </p>
                  {(contentBlock?.payload?.view_url || contentBlock?.payload?.file_url) && (
                    <p className="text-xs text-blue-600 dark:text-blue-300 mt-1 break-all">
                      {contentBlock.payload.view_url || contentBlock.payload.file_url}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-4">
                <div className="text-center p-6 bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-blue-300 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-600 transition-all duration-200">
                  <div className="text-blue-500 mb-3 text-4xl">📦</div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    Tải lên SCORM
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Kéo thả file vào đây hoặc click để chọn file
                  </p>
                  <input
                    type="file"
                    id="scorm-upload"
                    className="hidden"
                    accept={selectedType?.accept}
                    onChange={handleFileUpload}
                    disabled={isUploading}
                  />
                  <label
                    htmlFor="scorm-upload"
                    className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg cursor-pointer transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    Chọn File
                  </label>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                    Định dạng hỗ trợ: .zip
                  </p>
                </div>
                
                {/* URL Input as alternative */}
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Hoặc nhập URL SCORM:
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="url"
                      placeholder="https://example.com/scorm-package"
                      value={
                        scormUrl ||
                        ((contentBlock?.payload?.view_url || contentBlock?.payload?.file_url) &&
                        (contentBlock.payload.view_url || contentBlock.payload.file_url) !== null
                          ? contentBlock.payload.view_url || contentBlock.payload.file_url
                          : "")
                      }
                      onChange={(e) => setScormUrl(e.target.value)}
                      className="h-10 text-sm flex-1 placeholder:text-gray-400 placeholder:opacity-60 focus:placeholder:opacity-0"
                      disabled={isUploading}
                    />
                    <Button
                      onClick={handleUrlSubmit}
                      disabled={isUploading || !scormUrl.trim()}
                      className="h-10 px-4 text-sm font-medium bg-gradient-to-r from-gray-600 to-slate-600 hover:from-gray-700 hover:to-slate-700 transition-all duration-200"
                    >
                      {isUploading ? "⏳" : "Thêm"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upload Area for Image, Video, and Audio */}
          {(selectedContentType === "image" || selectedContentType === "video" || selectedContentType === "audio") && (
            <div className="p-6">
              {/* Show current file info when editing */}
              {(currentFile?.file_id || (contentBlock && hasValidPayload(contentBlock))) && (
                <div className="mb-4 p-3 bg-blue-100 dark:bg-blue-900/40 rounded-lg border border-blue-300 dark:border-blue-700">
                  <p className="text-xs font-medium text-blue-900 dark:text-blue-100 mb-1">File hiện tại:</p>
                  <p className="text-sm text-blue-800 dark:text-blue-200 font-semibold">
                    {contentBlock?.payload?.file_name || currentFile?.file_name || selectedType?.label}
                  </p>
                </div>
              )}

              <div className="text-center">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-100 to-slate-100 dark:from-gray-700 dark:to-slate-700 rounded-xl mb-4 shadow-sm">
                    <span className="text-3xl">{selectedType?.icon}</span>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {currentFile?.file_id || (contentBlock && hasValidPayload(contentBlock)) ? `Thay thế ${selectedType?.label}` : `Tải lên ${selectedType?.label}`}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                    Kéo thả file vào đây hoặc click để chọn file
                  </p>
                </div>

                <div className="flex items-center justify-center">
                  <label
                    className={`cursor-pointer inline-flex items-center px-6 py-3 text-sm font-semibold text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg ${
                      isUploading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                    }`}
                  >
                    {isUploading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Đang tải lên...
                      </>
                    ) : (
                      <>
                        <svg
                          className="mr-2 h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        Chọn File
                      </>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept={selectedType?.accept}
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                  </label>
                </div>

                <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
                  Định dạng hỗ trợ: {selectedType?.accept || "Nhiều định dạng"}
                </p>
              </div>
            </div>
          )}

          {/* Upload Area for other content types */}
          {selectedContentType !== "scorm" && selectedContentType !== "quiz" && selectedContentType !== "html" && selectedContentType !== "image" && selectedContentType !== "video" && selectedContentType !== "audio" && (
            <div className="p-6">
              {/* Show current file info when editing */}
              {(currentFile?.file_id || (contentBlock && hasValidPayload(contentBlock))) && (
                <div className="mb-4 p-3 bg-blue-100 dark:bg-blue-900/40 rounded-lg border border-blue-300 dark:border-blue-700">
                  <p className="text-xs font-medium text-blue-900 dark:text-blue-100 mb-1">File hiện tại:</p>
                  <p className="text-sm text-blue-800 dark:text-blue-200 font-semibold">
                    {contentBlock?.payload?.file_name || currentFile?.file_name || selectedType?.label}
                  </p>
                </div>
              )}

              <div className="text-center">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-100 to-slate-100 dark:from-gray-700 dark:to-slate-700 rounded-xl mb-4 shadow-sm">
                    <span className="text-3xl">{selectedType?.icon}</span>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {currentFile?.file_id || (contentBlock && hasValidPayload(contentBlock)) ? `Thay thế ${selectedType?.label}` : `Tải lên ${selectedType?.label}`}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                    Kéo thả file vào đây hoặc click để chọn file
                  </p>
                  {selectedContentType === "html_package" && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 -mt-3 mb-5">
                      Tải lên một file .html. Ảnh, CSS và JS cần nhúng sẵn trong file vì không tải lên kèm được.
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-center">
                  <label
                    className={`cursor-pointer inline-flex items-center px-6 py-3 text-sm font-semibold text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg ${
                      isUploading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                    }`}
                  >
                    {isUploading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Đang tải lên...
                      </>
                    ) : (
                      <>
                        <svg
                          className="mr-2 h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        Chọn File
                      </>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept={selectedType?.accept}
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                  </label>
                </div>

                <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
                  Định dạng hỗ trợ: {selectedType?.accept || "Nhiều định dạng"}
                </p>
              </div>
            </div>
          )}
            </div>
          )}
          </>
        )}

        {/* No Content Area */}
        {!hasContent && isEditMode && (
          <div className="p-4">
          {/* SCORM Upload/URL Mode */}
          {selectedContentType === "scorm" && (
            <div>
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <span className="mr-2">📦</span>
                  Tải lên SCORM Package
                </h4>
              </div>

              <div className="space-y-4">
                <div className="text-center p-6 bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-blue-300 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-600 transition-all duration-200">
                  <div className="text-blue-500 mb-3 text-4xl">📦</div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    Tải lên SCORM
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Kéo thả file vào đây hoặc click để chọn file
                  </p>
                  <input
                    type="file"
                    id="scorm-upload-new"
                    className="hidden"
                    accept=".zip"
                    onChange={handleFileUpload}
                    disabled={isUploading}
                  />
                  <label
                    htmlFor="scorm-upload-new"
                    className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg cursor-pointer transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    Chọn File
                  </label>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                    Định dạng hỗ trợ: .zip
                  </p>
                </div>
                
                {/* URL Input as alternative */}
                <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Hoặc nhập URL SCORM:
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="url"
                      placeholder="https://example.com/scorm-package"
                      value={scormUrl}
                      onChange={(e) => setScormUrl(e.target.value)}
                      className="h-10 text-sm flex-1 placeholder:text-gray-400 placeholder:opacity-60 focus:placeholder:opacity-0"
                      disabled={isUploading}
                    />
                    <Button
                      onClick={handleUrlSubmit}
                      disabled={isUploading || !scormUrl.trim()}
                      className="h-10 px-4 text-sm font-medium bg-gradient-to-r from-gray-600 to-slate-600 hover:from-gray-700 hover:to-slate-700 transition-all duration-200"
                    >
                      {isUploading ? "⏳" : "Thêm"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upload Area for Image, Video, and Audio */}
          {(selectedContentType === "image" || selectedContentType === "video" || selectedContentType === "audio") && (
            <div>
              <div className="text-center">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-100 to-slate-100 dark:from-gray-700 dark:to-slate-700 rounded-xl mb-4 shadow-sm">
                    <span className="text-3xl">{selectedType?.icon}</span>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Tải lên {selectedType?.label}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                    Kéo thả file vào đây hoặc click để chọn file
                  </p>
                </div>

                <div className="flex items-center justify-center">
                  <label
                    className={`cursor-pointer inline-flex items-center px-6 py-3 text-sm font-semibold text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg ${
                      isUploading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                    }`}
                  >
                    {isUploading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Đang tải lên...
                      </>
                    ) : (
                      <>
                        <svg
                          className="mr-2 h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        Chọn File
                      </>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept={selectedType?.accept}
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                  </label>
                </div>

                <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
                  Định dạng hỗ trợ: {selectedType?.accept || "Nhiều định dạng"}
                </p>
              </div>
            </div>
          )}

          {/* Upload Area for other content types */}
          {selectedContentType !== "scorm" && selectedContentType !== "quiz" && selectedContentType !== "html" && selectedContentType !== "image" && selectedContentType !== "video" && selectedContentType !== "audio" && (
            <div>
              <div className="text-center">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-100 to-slate-100 dark:from-gray-700 dark:to-slate-700 rounded-xl mb-4 shadow-sm">
                    <span className="text-3xl">{selectedType?.icon}</span>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Tải lên {selectedType?.label}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                    Kéo thả file vào đây hoặc click để chọn file
                  </p>
                  {selectedContentType === "html_package" && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 -mt-3 mb-5">
                      Tải lên một file .html. Ảnh, CSS và JS cần nhúng sẵn trong file vì không tải lên kèm được.
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-center">
                  <label
                    className={`cursor-pointer inline-flex items-center px-6 py-3 text-sm font-semibold text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg ${
                      isUploading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                    }`}
                  >
                    {isUploading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Đang tải lên...
                      </>
                    ) : (
                      <>
                        <svg
                          className="mr-2 h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        Chọn File
                      </>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept={selectedType?.accept}
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                  </label>
                </div>

                <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
                  Định dạng hỗ trợ: {selectedType?.accept || "Nhiều định dạng"}
                </p>
              </div>
            </div>
          )}
          </div>
        )}

        {/* No Content - Default State */}
        {!hasContent && !isEditMode && (
          <div className="text-center p-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-100 to-slate-100 dark:from-gray-700 dark:to-slate-700 rounded-xl mb-4 shadow-sm">
              <span className="text-3xl">{selectedType?.icon}</span>
            </div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
              Chọn loại nội dung và tải lên file để bắt đầu.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
